// RECRUITMENT-CANDIDATE-PROFILE-UI-002 — shared constants + small presentation atoms for the Candidate Profile drawer
import React from 'react';
import { formatDateOnly, formatDateTimeLocal } from '../../../../utils/formatters';

export const STAGE_ORDER = ['applied', 'screening', 'interview', 'offer', 'hired'];
export const STAGES = {
  applied:   { label: 'Applied',   chip: 'bg-slate-100 text-slate-700 border-slate-200' },
  screening: { label: 'Screening', chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  interview: { label: 'Interview', chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  offer:     { label: 'Offer',     chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  hired:     { label: 'Hired',     chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'Rejected',  chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};
export const stageLabel = (s) => STAGES[s]?.label || s || '—';
export const nextStageOf = (s) => {
  const i = STAGE_ORDER.indexOf(s);
  return i >= 0 && i < STAGE_ORDER.length - 1 ? STAGE_ORDER[i + 1] : null;
};

export const INTERVIEW_STATUS_BADGES = {
  scheduled: 'bg-blue-50 text-blue-700', completed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500', no_show: 'bg-rose-50 text-rose-700',
};
export const OUTCOME_BADGES = { pending: 'bg-amber-50 text-amber-700', passed: 'bg-emerald-50 text-emerald-700', failed: 'bg-rose-50 text-rose-700' };
export const OFFER_STATUS_BADGES = {
  pending: 'bg-amber-50 text-amber-700', accepted: 'bg-emerald-50 text-emerald-700',
  declined: 'bg-rose-50 text-rose-700', expired: 'bg-slate-100 text-slate-500',
};

export const ACTION_LABELS = {
  create: 'Candidate created', update: 'Profile updated', stage_change: 'Stage changed',
  interview_note_added: 'Note added', interview_scheduled: 'Interview scheduled', interview_updated: 'Interview updated',
  offer_created: 'Offer issued', offer_reissued: 'Offer re-issued', offer_status_changed: 'Offer status changed',
  resume_uploaded: 'Resume uploaded', resume_replaced: 'Resume replaced', resume_viewed: 'Resume viewed',
  converted_to_employee: 'Converted to employee', candidate_converted_to_employee: 'Converted to employee',
};

export const fmtDateTime = (d) => { if (!d) return '—'; try { return formatDateTimeLocal(d); } catch { return d; } };
export const fmtDate = (d) => { if (!d) return '—'; try { return formatDateOnly(d); } catch { return d; } };
export const initialsOf = (name) => (name || '?').split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
export const fileTypeOf = (name) => ((name || '').split('.').pop() || '').toUpperCase();

export const StageChip = ({ stage, className = '', ...rest }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STAGES[stage]?.chip || STAGES.applied.chip} ${className}`} {...rest}>
    {stageLabel(stage)}
  </span>
);

export const Chip = ({ children, tone = 'bg-slate-100 text-slate-600', className = '', ...rest }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${tone} ${className}`} {...rest}>{children}</span>
);

export const SmallLabel = ({ children, className = '' }) => (
  <p className={`text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500 ${className}`}>{children}</p>
);

export const Card = ({ title, icon: Icon, action, children, className = '', bodyClassName = '', ...rest }) => (
  <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`} {...rest}>
    {(title || action) && (
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
        <h3 className="sf-heading flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
          {Icon && <Icon className="h-4 w-4 text-slate-400" />}{title}
        </h3>
        {action}
      </header>
    )}
    <div className={`px-5 py-4 ${bodyClassName}`}>{children}</div>
  </section>
);

// Definition-list style field grid — label above value, no per-field icons
export const FieldGrid = ({ children, cols = 2 }) => (
  <dl className={`grid grid-cols-1 gap-x-6 gap-y-4 ${cols === 2 ? 'sm:grid-cols-2' : cols === 3 ? 'sm:grid-cols-3' : ''}`}>{children}</dl>
);
export const Field = ({ label, value, muted, testId, full }) => {
  const empty = value === null || value === undefined || value === '' || value === '—';
  return (
    <div className={`min-w-0 ${full ? 'sm:col-span-2' : ''}`} data-testid={testId}>
      <dt className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">{label}</dt>
      <dd className={`mt-0.5 break-words text-sm ${empty || muted ? 'text-slate-400' : 'font-medium text-slate-900'}`}>{empty ? '—' : value}</dd>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, hint, action, testId }) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/70 px-6 py-8 text-center" data-testid={testId}>
    {Icon && <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200"><Icon className="h-5 w-5" /></div>}
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    {hint && <p className="mt-1 max-w-sm text-xs leading-relaxed text-slate-500">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
